import { useModalShoppingStore } from '@/store/shopping/modalShoppingStore';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    LuCalendar,
    LuChevronDown,
    LuChevronUp,
    LuTrash,
} from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';
import { Render } from '../../helpers/Render';
import { DateUtils } from '../../helpers/dateUtils';
import { Format } from '../../helpers/formatters';
import { useModalParkStore } from '../../store/park/modalParkStore';
import { useModalServicesStore } from '../../store/services/modalServicesStore';
import { useShoppingCartStore } from '../../store/shoppingCart/shoppingCartStore';
import { BigButton } from '../BigButton';
import ReactIcon from '../ReactIcon';
import {
    ActionsButtonsFooterWrap,
    ButtonsWrapper,
    ContainerWrap,
    ElementTotalSum,
    TextDate,
    TextValue,
    ValuesNumbersWrap,
    ValuesWrap,
    WrapElementsCartSmallScreen,
    WrapIconText,
} from './styles';

type Props = {
    shopVisible: boolean;
    setShopVisible: () => void;
};

export const ItemsCartSmallScreen: React.FC<Props> = ({
    shopVisible,
    setShopVisible,
}) => {
    const navigate = useNavigate();

    const { cartValues, setCurrentIdRemoving } = useShoppingCartStore();
    const { isVisibleShoppingWave, setIsVisibleShoppingWave } =
        useModalShoppingStore();
    const { setIsVisibleServices } = useModalServicesStore();
    const { setIsVisiblePark } = useModalParkStore();

    const [isVisible, setIsVisible] = useState(true);
    const refToWrap = useRef<HTMLDivElement>(null);

    const matchQuery = useMediaQuery('(min-width: 768px)');

    // Soma de todos os valores no carrinho
    const sumValuesCard = useMemo(() => {
        if (!cartValues) return 0;

        return cartValues.reduce(
            (accumulator, item) => accumulator + item.value,
            0
        );
    }, [cartValues]);

    const handleScroll = useCallback(() => {
        if (refToWrap.current && refToWrap.current.scrollTop > 1) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    }, []);

    function handleNavigateCheckout() {
        setIsVisibleServices(false);
        setIsVisibleShoppingWave(false);
        setIsVisiblePark(false);
        navigate('/checkout');
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }

    useEffect(() => {
        if (matchQuery && shopVisible) {
            setShopVisible();
        }
    }, [matchQuery, setShopVisible, shopVisible]);

    useEffect(() => {
        const currentRef = refToWrap.current;
        if (currentRef) {
            currentRef.addEventListener('scroll', handleScroll);
        }

        // Tive que fazer uma aproximação
        // Mesmo sem scroll o scrollHeigh ainda tinha valor diferente em alguns casos
        if (
            currentRef &&
            currentRef?.scrollHeight - currentRef?.clientHeight < 30 &&
            !isVisible
        ) {
            setIsVisible(true);
        }

        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll, shopVisible, isVisible, refToWrap, cartValues]);

    const isVoucher = sumValuesCard === 0 && (cartValues ?? []).length > 0;

    return (
        <ActionsButtonsFooterWrap ref={refToWrap}>
            <ValuesWrap>
                <ValuesNumbersWrap>
                    <span>Itens do carrinho ({cartValues?.length || 0})</span>
                    <ReactIcon
                        Icon={shopVisible ? LuChevronUp : LuChevronDown}
                        cursor={'pointer'}
                        onClick={() => setShopVisible()}
                    />
                </ValuesNumbersWrap>

                <Render show={shopVisible && !!cartValues}>
                    {cartValues?.map(item => (
                        <WrapElementsCartSmallScreen key={item.id}>
                            <div>
                                <h2>{item.title}</h2>
                                <WrapIconText>
                                    <ReactIcon
                                        Icon={LuCalendar}
                                        size={'1rem'}
                                    />
                                    <TextDate>
                                        {DateUtils.formatPossibleDate(
                                            item.date
                                        )}
                                    </TextDate>
                                </WrapIconText>
                            </div>
                            <WrapIconText>
                                <TextValue>
                                    {Format.currency(item.value)}
                                </TextValue>
                                <ReactIcon
                                    Icon={LuTrash}
                                    size={'1.25rem'}
                                    cursor={'pointer'}
                                    onClick={() =>
                                        setCurrentIdRemoving(item.idCart!)
                                    }
                                />
                            </WrapIconText>
                        </WrapElementsCartSmallScreen>
                    ))}
                </Render>
            </ValuesWrap>

            <ElementTotalSum>
                <span>Valor total</span>
                <span>{Format.currency(sumValuesCard)}</span>
            </ElementTotalSum>

            <ContainerWrap>
                <ButtonsWrapper isVisible={isVisible || !shopVisible}>
                    <BigButton
                        color={cartValues?.length ? 'primary' : 'secondary'}
                        bgColor={cartValues?.length ? 'primary' : 'secondary'}
                        children={
                            isVoucher ? 'Resgatar voucher' : 'Ir para pagamento'
                        }
                        disabled={!cartValues?.length}
                        cursorBlocked={!cartValues?.length}
                        padding="0.5rem 0.75rem"
                        allWidth
                        onClick={handleNavigateCheckout}
                    />

                    {isVisibleShoppingWave && (
                        <BigButton
                            color="primary"
                            bgColor="secondary"
                            children={'Adquirir outros serviços'}
                            onClick={() => {
                                setIsVisibleShoppingWave(false);
                                setIsVisibleServices(true);
                            }}
                            padding="0.5rem 0.75rem"
                            allWidth
                        />
                    )}
                </ButtonsWrapper>
            </ContainerWrap>
        </ActionsButtonsFooterWrap>
    );
};
